import React, { useEffect, useMemo, useState } from 'react';
import { useDebugStore } from '../../stores/debugStore';
import { useGameStore } from '../../stores/gameStore';
import {
  CHEAT_BG,
  CHEAT_BG_ACTIVE,
  CHEAT_BORDER,
  CHEAT_BORDER_ACTIVE,
  CHEAT_LABEL_TEXT,
  CHEAT_TEXT,
  cheatButtonStyle,
} from '../../constants/cheatStyles';

// The item list scrolls inside the section so the inventory panel never grows unbounded.
const ITEM_LIST_MAX_HEIGHT_PX = 160;
// The catalog holds every def in the game; only the first slice is rendered so a broad
// filter (or an empty one) stays responsive.
const CATALOG_RESULT_LIMIT = 60;

const STATUS_ERROR_TEXT = '#ff8888';
const STATUS_SUCCESS_TEXT = '#88ff88';

const sectionLabelStyle: React.CSSProperties = {
  color: CHEAT_LABEL_TEXT,
  fontFamily: 'monospace',
  fontSize: 9,
  letterSpacing: 1,
  marginBottom: 4,
};

const fieldStyle: React.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  background: 'rgba(10, 10, 20, 0.85)',
  border: `1px solid ${CHEAT_BORDER}`,
  borderRadius: 4,
  color: CHEAT_TEXT,
  fontFamily: 'monospace',
  fontSize: 11,
  padding: '3px 6px',
  outline: 'none',
};

/**
 * Development affordance for putting the game into a testable state: grant any item def
 * directly into the bag, or roll any loot table on demand. Both are permanent debug
 * tools, gated on the server by `cheatsAllowed` and hidden here behind the cheat toggle.
 *
 * It lives at the foot of the inventory panel because that is where its results land —
 * granted items appear in the bag directly above it. Collapsed it is an amber bar; the
 * catalog is fetched lazily the first time it is expanded.
 */
export const GrantCheatSection: React.FC = () => {
  const [expanded, setExpanded] = useState(false);
  const [filter, setFilter] = useState('');
  const [selectedTableId, setSelectedTableId] = useState('');

  const gameId = useGameStore((s) => s.gameState?.id);
  const items = useDebugStore((s) => s.items);
  const lootTables = useDebugStore((s) => s.lootTables);
  const loadingCatalog = useDebugStore((s) => s.loadingCatalog);
  const busy = useDebugStore((s) => s.busy);
  const status = useDebugStore((s) => s.status);
  const loadCatalog = useDebugStore((s) => s.loadCatalog);
  const grantItem = useDebugStore((s) => s.grantItem);
  const grantLoot = useDebugStore((s) => s.grantLoot);

  useEffect(() => {
    if (expanded && gameId) void loadCatalog(gameId);
  }, [expanded, gameId, loadCatalog]);

  // Default the loot select to the first table once the catalog arrives.
  useEffect(() => {
    if (!selectedTableId && lootTables.length > 0) setSelectedTableId(lootTables[0].id);
  }, [lootTables, selectedTableId]);

  const matches = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    const filtered = needle
      ? items.filter(i => i.name.toLowerCase().includes(needle) || i.id.toLowerCase().includes(needle))
      : items;
    return filtered.slice(0, CATALOG_RESULT_LIMIT);
  }, [items, filter]);

  if (!gameId) return null;

  const hiddenCount = items.length - matches.length;

  return (
    <div style={{ marginTop: 8, flexShrink: 0, fontFamily: 'monospace' }}>
      <div
        onClick={() => setExpanded(prev => !prev)}
        title="Cheat: grant items and roll loot tables"
        style={{
          background: expanded ? CHEAT_BG_ACTIVE : 'rgba(10, 10, 20, 0.75)',
          border: `1px ${expanded ? 'solid' : 'dashed'} ${expanded ? CHEAT_BORDER_ACTIVE : CHEAT_BORDER}`,
          borderRadius: 6,
          padding: '4px 10px',
          fontSize: 12,
          color: expanded ? CHEAT_TEXT : CHEAT_BORDER,
          cursor: 'pointer',
          userSelect: 'none',
          whiteSpace: 'nowrap',
          textAlign: 'center',
        }}
      >
        🎁 Grant
      </div>

      {expanded && (
        <div style={{
          marginTop: 6,
          background: 'rgba(10, 10, 20, 0.92)',
          border: `1px dashed ${CHEAT_BORDER}`,
          borderRadius: 8,
          padding: 8,
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
        }}>
          {/* Grant Item */}
          <div>
            <div style={sectionLabelStyle}>GRANT ITEM</div>
            <input
              type="text"
              value={filter}
              placeholder="Filter by name or id…"
              onChange={(e) => setFilter(e.target.value)}
              style={fieldStyle}
            />
            <div style={{
              marginTop: 4,
              maxHeight: ITEM_LIST_MAX_HEIGHT_PX,
              overflowY: 'auto',
              border: `1px solid ${CHEAT_BORDER}`,
              borderRadius: 4,
              background: CHEAT_BG,
            }}>
              {loadingCatalog && (
                <div style={{ color: CHEAT_LABEL_TEXT, fontSize: 11, padding: 6 }}>Loading catalog…</div>
              )}
              {!loadingCatalog && matches.length === 0 && (
                <div style={{ color: CHEAT_LABEL_TEXT, fontSize: 11, padding: 6 }}>No matching items.</div>
              )}
              {matches.map(item => (
                <div
                  key={item.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 6,
                    padding: '3px 4px',
                    borderBottom: `1px solid ${CHEAT_BG}`,
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div style={{ color: CHEAT_TEXT, fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {item.name}
                    </div>
                    <div style={{ color: CHEAT_LABEL_TEXT, fontSize: 9, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {item.id}
                    </div>
                  </div>
                  <button
                    onClick={() => gameId && grantItem(gameId, item.id)}
                    disabled={busy}
                    style={{ ...cheatButtonStyle, opacity: busy ? 0.5 : 1, cursor: busy ? 'wait' : 'pointer' }}
                  >
                    Grant
                  </button>
                </div>
              ))}
            </div>
            {hiddenCount > 0 && (
              <div style={{ color: CHEAT_LABEL_TEXT, fontSize: 9, marginTop: 3 }}>
                {hiddenCount} more — narrow the filter to see them.
              </div>
            )}
          </div>

          {/* Grant Loot */}
          <div>
            <div style={sectionLabelStyle}>GRANT LOOT</div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <select
                value={selectedTableId}
                onChange={(e) => setSelectedTableId(e.target.value)}
                style={{ ...fieldStyle, flex: 1, minWidth: 0 }}
              >
                {lootTables.map(table => (
                  <option key={table.id} value={table.id}>{table.name}</option>
                ))}
              </select>
              <button
                onClick={() => gameId && selectedTableId && grantLoot(gameId, selectedTableId)}
                disabled={busy || !selectedTableId}
                style={{
                  ...cheatButtonStyle,
                  opacity: busy || !selectedTableId ? 0.5 : 1,
                  cursor: busy ? 'wait' : 'pointer',
                }}
              >
                Roll
              </button>
            </div>
          </div>

          {status && (
            <div style={{
              fontSize: 11,
              color: status.error ? STATUS_ERROR_TEXT : STATUS_SUCCESS_TEXT,
            }}>
              {status.message}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
